import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Banner, BlockStack, Box, Button, ButtonGroup, DropZone, InlineStack, Text, TextField } from '@shopify/polaris';

const MAX_BYTES = 2 * 1024 * 1024;

const ImageField = ({ label, url, file, onUrlChange, onFileChange, shape, hint, error, exclusive, required }) => {
  const [mode, setMode] = useState(null);
  const [rejected, setRejected] = useState(null);

  const objectUrl = useMemo(() => (file ? window.URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (objectUrl) window.URL.revokeObjectURL(objectUrl); }, [objectUrl]);

  const preview = exclusive ? objectUrl || url : url || objectUrl;
  const host = (() => { try { return new URL(url).hostname; } catch { return ''; } })();
  // On the domain boundary, or a host such as evils3.amazonaws.com would be called ours.
  const hostedByUs = host === 's3.amazonaws.com' || host.endsWith('.s3.amazonaws.com');
  const isIcon = shape === 'icon';

  return (
    <BlockStack gap="300">
      <Text as="span" variant="bodyMd" fontWeight="medium">
        {label}
        {required && <Text as="span" tone="critical"> *</Text>}
      </Text>

      <InlineStack gap="400" blockAlign="start" wrap>
        <BlockStack gap="100" inlineAlign="center">
          <div
            style={{
              width: isIcon ? 60 : 170,
              height: isIcon ? 60 : 100,
              borderRadius: isIcon ? '50%' : 8,
              overflow: 'hidden',
              background: 'var(--p-color-bg-fill-tertiary)',
              border: '1px solid var(--p-color-border)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {preview
              ? <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <Text as="span" variant="bodySm" tone="subdued">none</Text>}
          </div>
          <Text as="span" variant="bodySm" tone="subdued">
            {isIcon ? '60 × 60' : '170 px wide'}
          </Text>
        </BlockStack>

        <BlockStack gap="200">
          <ButtonGroup>
            <Button size="slim" pressed={mode === 'upload'} onClick={() => setMode(mode === 'upload' ? null : 'upload')}>
              Upload image
            </Button>
            <Button size="slim" pressed={mode === 'link'} onClick={() => setMode(mode === 'link' ? null : 'link')}>
              Use a link
            </Button>
            {(url || file) && (
              <Button size="slim" tone="critical" variant="plain" onClick={() => { onFileChange(null); onUrlChange(''); }}>
                Remove
              </Button>
            )}
          </ButtonGroup>

          {mode === 'upload' && (
            <DropZone
              allowMultiple={false}
              accept="image/*"
              type="image"
              onDrop={(files, acceptedFiles) => {
                // `files` includes what the accept filter rejected; only acceptedFiles passed it.
                const [accepted] = acceptedFiles;

                if (!accepted) return setRejected('That file is not an image.');
                if (accepted.size > MAX_BYTES) return setRejected('That image is over 2 MB.');

                setRejected(null);
                if (exclusive) onUrlChange('');
                onFileChange(accepted);

                return setMode(null);
              }}
            >
              <DropZone.FileUpload actionTitle="Drop an image, or click to choose" actionHint="PNG, JPG or WebP · up to 2 MB · hosted by us" />
            </DropZone>
          )}

          {rejected && (
            <Banner tone="warning" onDismiss={() => setRejected(null)}>
              <Text as="span" variant="bodySm">{rejected}</Text>
            </Banner>
          )}

          {mode === 'link' && (
            <TextField
              label="Image address"
              labelHidden
              autoComplete="off"
              placeholder="https://…"
              value={url || ''}
              onChange={(value) => {
                if (exclusive) onFileChange(null);
                onUrlChange(value);
              }}
              error={error}
              helpText="Saved as typed. We do not copy it, so if that site removes the file the image breaks here too."
            />
          )}

          {url && !file && (
            <Box>
              <Banner tone={hostedByUs ? 'success' : 'warning'}>
                <Text as="span" variant="bodySm">
                  {hostedByUs
                    ? 'Hosted by us. It cannot disappear.'
                    : `Hosted on ${host || 'another site'}, not by us. If that address changes the image breaks and nobody is told.`}
                </Text>
              </Banner>
            </Box>
          )}
          {error && <Text as="span" variant="bodySm" tone="critical">{error}</Text>}

          <Text as="span" variant="bodySm" tone="subdued">{hint}</Text>
        </BlockStack>
      </InlineStack>
    </BlockStack>
  );
};

ImageField.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  url: PropTypes.string,
  file: PropTypes.object,
  exclusive: PropTypes.bool,
  onUrlChange: PropTypes.func.isRequired,
  onFileChange: PropTypes.func.isRequired,
  shape: PropTypes.oneOf(['icon', 'wide']).isRequired,
  hint: PropTypes.string,
  error: PropTypes.string,
};

export default ImageField;
